import {
  all,
  call,
  put,
  select,
  takeEvery,
  takeLatest,
} from "redux-saga/effects";
import api from "../../utils/api";
import { errorAlert, successAlert } from "../Alerts/actions";
import { fetchCareHearthDisputes } from "../CareHearthBuyers/actions";
import {
  createCareHearthOffersFailure,
  createCareHearthOffersSuccess,
  decideCareHearthDisputeFailure,
  decideCareHearthDisputeSuccess,
  fetchCareHearthEnquiriesFailure,
  fetchCareHearthEnquiriesSuccess,
  fetchCareHearthAuditEvents,
  fetchCareHearthAuditEventsFailure,
  fetchCareHearthAuditEventsSuccess,
  fetchCareHearthEnquiry,
  fetchCareHearthEnquiryFailure,
  fetchCareHearthEnquirySuccess,
  fetchCareHearthSearchesFailure,
  fetchCareHearthSearchesSuccess,
  findCareHearthEligibleHomesFailure,
  findCareHearthEligibleHomesSuccess,
  recordCareHearthSearchCallFailure,
  recordCareHearthSearchCallSuccess,
  startCareHearthSearchEnquiryFailure,
  startCareHearthSearchEnquirySuccess,
  updateCareHearthLifecycleFailure,
  updateCareHearthLifecycleSuccess,
  updateCareHearthVerificationFailure,
  updateCareHearthVerificationSuccess,
} from "./actions";
import {
  CREATE_CARE_HEARTH_OFFERS,
  DECIDE_CARE_HEARTH_DISPUTE,
  FETCH_CARE_HEARTH_ENQUIRIES,
  FETCH_CARE_HEARTH_ENQUIRY,
  FETCH_CARE_HEARTH_SEARCHES,
  FETCH_CARE_HEARTH_AUDIT_EVENTS,
  FIND_CARE_HEARTH_ELIGIBLE_HOMES,
  RECORD_CARE_HEARTH_SEARCH_CALL,
  START_CARE_HEARTH_SEARCH_ENQUIRY,
  UPDATE_CARE_HEARTH_LIFECYCLE,
  UPDATE_CARE_HEARTH_VERIFICATION,
} from "./constants";

const getErrorMessage = (error, fallback) => {
  if (typeof error === "string") return error;
  return error?.error || error?.message || fallback;
};

const getToken = (state) => state?.AuthReducer?.token;

export function* fetchCareHearthEnquiriesSaga({ payload }) {
  const token = yield select(getToken);

  try {
    const response = yield call(api(token).get, "/api/admin/hearth/enquiries", {
      params: payload,
    });
    yield put(
      fetchCareHearthEnquiriesSuccess({
        items: response?.data?.items || [],
        pagination: response?.data?.pagination || { last_page: 1, total: 0 },
        counts: response?.data?.counts || null,
      }),
    );
  } catch (error) {
    yield put(
      fetchCareHearthEnquiriesFailure(
        getErrorMessage(error, "Could not load the CareHearth marketplace."),
      ),
    );
  }
}

export function* fetchCareHearthEnquirySaga({ payload: { uuid } }) {
  const token = yield select(getToken);

  try {
    const [detail, events] = yield all([
      call(api(token).get, `/api/admin/hearth/enquiries/${uuid}`),
      call(api(token).get, `/api/admin/hearth/enquiries/${uuid}/audit-events`),
    ]);
    yield put(
      fetchCareHearthEnquirySuccess({
        audit: events?.data?.items || [],
        enquiry: detail?.data || null,
      }),
    );
  } catch (error) {
    yield put(
      fetchCareHearthEnquiryFailure(
        getErrorMessage(error, "Could not load this marketplace workspace."),
      ),
    );
  }
}

export function* fetchCareHearthAuditEventsSaga({ payload: { uuid } }) {
  const token = yield select(getToken);

  try {
    const events = yield call(
      api(token).get,
      `/api/admin/hearth/enquiries/${uuid}/audit-events`,
    );
    yield put(fetchCareHearthAuditEventsSuccess(events?.data?.items || []));
  } catch (error) {
    yield put(
      fetchCareHearthAuditEventsFailure(
        getErrorMessage(error, "Could not refresh marketplace activity."),
      ),
    );
  }
}

export function* findCareHearthEligibleHomesSaga({
  payload: { uuid, refresh = false },
}) {
  const token = yield select(getToken);

  try {
    const response = yield call(
      api(token).post,
      `/api/admin/hearth/enquiries/${uuid}/eligible-homes`,
      refresh ? { refresh: true } : undefined,
    );
    yield put(findCareHearthEligibleHomesSuccess(response?.data || null));
    // Evaluating a match writes marketplace activity. Refresh just that list so
    // its tab count is immediately correct without resetting the workspace.
    if (!response?.data?.reused) {
      yield put(fetchCareHearthAuditEvents({ uuid }));
    }
  } catch (error) {
    yield put(
      findCareHearthEligibleHomesFailure(
        getErrorMessage(
          error,
          "Could not evaluate eligible homes. Please try again.",
        ),
      ),
    );
  }
}

export function* createCareHearthOffersSaga({ payload: { uuid, data } }) {
  const token = yield select(getToken);

  try {
    yield call(
      api(token).post,
      `/api/admin/hearth/enquiries/${uuid}/offers`,
      data,
    );
    const message = `${data.location_ids.length} manual offer(s) created after live eligibility recheck.`;
    yield put(successAlert(message));
    yield put(
      createCareHearthOffersSuccess({
        message,
        type: "offers",
      }),
    );
    yield put(fetchCareHearthEnquiry({ uuid }));
  } catch (error) {
    const message = getErrorMessage(
      error,
      "Could not create the selected offers.",
    );
    yield put(errorAlert(message));
    yield put(createCareHearthOffersFailure(message));
  }
}

export function* updateCareHearthLifecycleSaga({
  payload: { uuid, name, data },
}) {
  const token = yield select(getToken);

  try {
    yield call(
      api(token).post,
      `/api/admin/hearth/enquiries/${uuid}/${name}`,
      data,
    );
    const message =
      name === "revalidate-relist"
        ? "Family confirmation and fresh verification recorded; enquiry relisted."
        : `Listing ${name}d.`;
    yield put(successAlert(message));
    yield put(
      updateCareHearthLifecycleSuccess({
        message,
        type: "lifecycle",
      }),
    );
    yield put(fetchCareHearthEnquiry({ uuid }));
  } catch (error) {
    const message = getErrorMessage(error, `Could not ${name} this listing.`);
    yield put(errorAlert(message));
    yield put(updateCareHearthLifecycleFailure(message));
  }
}

export function* updateCareHearthVerificationSaga({
  payload: { uuid, action, note, called_at: calledAt },
}) {
  const token = yield select(getToken);

  try {
    yield call(
      api(token).post,
      `/api/admin/hearth/enquiries/${uuid}/verification`,
      {
        action,
        ...(note ? { note } : {}),
        ...(calledAt ? { called_at: calledAt } : {}),
      },
    );
    const message =
      action === "call"
        ? "Call recorded. The enquiry is now manually verified."
        : "Verification level recalculated from the family's proofs.";
    yield put(successAlert(message));
    yield put(
      updateCareHearthVerificationSuccess({
        message,
        type: "verification",
      }),
    );
    yield put(fetchCareHearthEnquiry({ uuid }));
  } catch (error) {
    const message = getErrorMessage(error, "Could not update verification.");
    yield put(errorAlert(message));
    yield put(updateCareHearthVerificationFailure(message));
  }
}

// A home's dispute of a bought enquiry (15 October 2026). Approve voids the
// enquiry for every home and gives every buyer their money back; reject is
// final and sends the home our note.
// From the Disputes list (listParams) the list is reloaded, else the enquiry.
export function* decideCareHearthDisputeSaga({
  payload: { uuid, disputeUuid, decision, note, listParams },
}) {
  const token = yield select(getToken);
  const approve = decision === "approve";

  try {
    yield call(
      api(token).post,
      `/api/admin/hearth/enquiries/${uuid}/disputes/${disputeUuid}/${approve ? "approve" : "reject"}`,
      note ? { note } : {},
    );
    const message = approve
      ? "Dispute approved. The enquiry is voided and every home that bought it gets its money back."
      : "Dispute rejected. The home has been sent your note.";
    yield put(successAlert(message));
    yield put(
      decideCareHearthDisputeSuccess({
        message,
        type: "dispute",
      }),
    );
    yield put(
      listParams
        ? fetchCareHearthDisputes(listParams)
        : fetchCareHearthEnquiry({ uuid }),
    );
  } catch (error) {
    const message = getErrorMessage(
      error,
      approve
        ? "Could not approve this dispute."
        : "Could not reject this dispute.",
    );
    yield put(errorAlert(message));
    yield put(decideCareHearthDisputeFailure(message));
  }
}

// Not sent (15 October 2026): /find searches whose family kept "Send my
// enquiry" off, so no enquiry exists. Those with a mobile come first.
export function* fetchCareHearthSearchesSaga({ payload }) {
  const token = yield select(getToken);

  try {
    const response = yield call(api(token).get, "/api/admin/hearth/searches", {
      params: payload,
    });
    yield put(
      fetchCareHearthSearchesSuccess({
        items: response?.data?.items || [],
        pagination: response?.data?.pagination || { last_page: 1, total: 0 },
        counts: response?.data?.counts || null,
      }),
    );
  } catch (error) {
    yield put(
      fetchCareHearthSearchesFailure(
        getErrorMessage(error, "Could not load the searches not sent."),
      ),
    );
  }
}

const SEARCH_CALL_OUTCOMES = {
  reached: "Reached",
  no_answer: "No answer",
  wrong_number: "Wrong number",
  not_interested: "Not interested",
};

export function* recordCareHearthSearchCallSaga({
  payload: { uuid, outcome, note },
}) {
  const token = yield select(getToken);

  try {
    const response = yield call(
      api(token).post,
      `/api/admin/hearth/searches/${uuid}/calls`,
      { outcome, ...(note ? { note } : {}) },
    );
    const item = response?.data || null;
    const label = SEARCH_CALL_OUTCOMES[outcome] || outcome;
    const message =
      item?.in_queue === false
        ? `Call recorded: ${label}. The search has left Not sent.`
        : `Call recorded: ${label}.`;
    yield put(successAlert(message));
    yield put(
      recordCareHearthSearchCallSuccess({
        item,
        message,
        type: "search_call",
      }),
    );
  } catch (error) {
    const message = getErrorMessage(error, "Could not record the call.");
    yield put(errorAlert(message));
    yield put(recordCareHearthSearchCallFailure(message));
  }
}

// Starts an enquiry from the search after our team has spoken to the family:
// Started by our team, verified by the call. Homes see nothing until the team
// adds or sends homes from Call and homes.
export function* startCareHearthSearchEnquirySaga({ payload: { uuid, note } }) {
  const token = yield select(getToken);

  try {
    const response = yield call(
      api(token).post,
      `/api/admin/hearth/searches/${uuid}/enquiry`,
      { note },
    );
    const enquiryUuid = response?.data?.enquiry_uuid;
    if (!enquiryUuid) throw new Error("The enquiry could not be opened.");

    const existing = Boolean(response?.data?.existing);
    const message = existing
      ? "This search already has an enquiry. Opening it."
      : "Enquiry started. Add or send homes from Call and homes.";
    yield put(successAlert(message));
    yield put(
      startCareHearthSearchEnquirySuccess({
        enquiryUuid,
        existing,
        message,
        searchUuid: uuid,
        type: "search_enquiry",
      }),
    );
  } catch (error) {
    const message = getErrorMessage(error, "Could not start the enquiry.");
    yield put(errorAlert(message));
    yield put(startCareHearthSearchEnquiryFailure(message));
  }
}

export default function* CareHearthSaga() {
  yield all([
    takeLatest(FETCH_CARE_HEARTH_ENQUIRIES, fetchCareHearthEnquiriesSaga),
    takeLatest(FETCH_CARE_HEARTH_ENQUIRY, fetchCareHearthEnquirySaga),
    takeLatest(FETCH_CARE_HEARTH_AUDIT_EVENTS, fetchCareHearthAuditEventsSaga),
    takeLatest(FETCH_CARE_HEARTH_SEARCHES, fetchCareHearthSearchesSaga),
    takeLatest(
      FIND_CARE_HEARTH_ELIGIBLE_HOMES,
      findCareHearthEligibleHomesSaga,
    ),
    takeEvery(CREATE_CARE_HEARTH_OFFERS, createCareHearthOffersSaga),
    takeEvery(DECIDE_CARE_HEARTH_DISPUTE, decideCareHearthDisputeSaga),
    takeEvery(RECORD_CARE_HEARTH_SEARCH_CALL, recordCareHearthSearchCallSaga),
    takeEvery(
      START_CARE_HEARTH_SEARCH_ENQUIRY,
      startCareHearthSearchEnquirySaga,
    ),
    takeEvery(UPDATE_CARE_HEARTH_LIFECYCLE, updateCareHearthLifecycleSaga),
    takeEvery(
      UPDATE_CARE_HEARTH_VERIFICATION,
      updateCareHearthVerificationSaga,
    ),
  ]);
}
